// Savings accordion — same structure as CostAccordion, grouped by savings
// category, with a search/add bar (category suggestions), filter chips, and
// per-section annualised footer totals. No P&L badge (savings are revenue).

import { useMemo, useState } from 'react';
import { Icon } from '../Icon';
import type {
  PortalSavingsCategory,
  PortalSavingsCreateRequest,
  PortalSavingsItem,
  PortalSavingsUpdateRequest,
} from '../../api/types';
import { SavingsDetailPanel } from './SavingsDetailPanel';
import {
  annualisedSavingsTotal,
  groupByCategory,
  intervalLabel,
  type FilterChip,
} from './helpers';

interface SavingsAccordionProps {
  savings: PortalSavingsItem[];
  categories: PortalSavingsCategory[];
  formatCurrency: (value: number | null | undefined) => string;
  readOnly: boolean;
  onCreate: (body: PortalSavingsCreateRequest) => Promise<void>;
  onUpdate: (savingsId: string, body: PortalSavingsUpdateRequest) => Promise<void>;
  onDelete: (savingsId: string) => Promise<void>;
}

type Editing =
  | { mode: 'create'; category: PortalSavingsCategory }
  | { mode: 'edit'; category: PortalSavingsCategory; item: PortalSavingsItem }
  | null;

export function SavingsAccordion({
  savings,
  categories,
  formatCurrency,
  readOnly,
  onCreate,
  onUpdate,
  onDelete,
}: SavingsAccordionProps) {
  const [filter, setFilter] = useState<FilterChip>('all');
  const [query, setQuery] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);

  const categoryById = useMemo(() => {
    const m = new Map<string, PortalSavingsCategory>();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  const filtered = useMemo(() => {
    if (filter === 'one_time') return savings.filter((s) => !s.recurring);
    if (filter === 'recurring') return savings.filter((s) => s.recurring);
    return savings;
  }, [savings, filter]);

  const sections = useMemo(
    () => groupByCategory(filtered, (s) => s.category?.name ?? 'Uncategorised'),
    [filtered],
  );

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return inputFocused ? categories.slice(0, 8) : [];
    return categories
      .filter((c) => (c.name ?? '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [categories, query, inputFocused]);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-sm font-bold text-ink">Savings</div>
        <div className="flex gap-1.5" role="group" aria-label="Filter savings">
          {(['all', 'one_time', 'recurring'] as FilterChip[]).map((chip) => {
            const on = filter === chip;
            const label = chip === 'all' ? 'All' : chip === 'one_time' ? 'One-time' : 'Recurring';
            return (
              <button
                key={chip}
                onClick={() => setFilter(chip)}
                aria-pressed={on}
                className={`rounded-lg border-[1.5px] px-2.5 py-1 text-[0.72rem] font-semibold ${
                  on
                    ? 'border-accent bg-accent-light text-accent'
                    : 'border-line bg-white text-muted hover:border-accent hover:text-accent'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {!readOnly && (
        <div className="relative mb-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            placeholder="Search savings categories to add…"
            aria-label="Search savings categories"
            className="w-full rounded-[9px] border-[1.5px] border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-accent"
          />
          {suggestions.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-[9px] border border-line bg-white shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
              {suggestions.map((c) => (
                <li key={c.id}>
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setEditing({ mode: 'create', category: c });
                      setQuery('');
                      setInputFocused(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-panel"
                  >
                    <Icon name="plus" size={11} /> {c.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {sections.length === 0 ? (
        <div className="rounded-[10px] border border-dashed border-line bg-panel px-3 py-6 text-center text-sm text-muted">
          No savings yet.{!readOnly && ' Search a category above to add one.'}
        </div>
      ) : (
        <div className="space-y-3">
          {sections.map((section) => (
            <div key={section.category} className="overflow-hidden rounded-[10px] border border-line">
              <div className="flex items-center justify-between bg-panel px-3 py-2">
                <span className="text-sm font-bold text-ink">{section.category}</span>
                <span className="text-xs font-semibold text-muted">
                  {formatCurrency(annualisedSavingsTotal(section.items))}/yr
                </span>
              </div>
              <ul className="divide-y divide-line">
                {section.items.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => {
                        const category =
                          categoryById.get(s.category_id ?? '') ?? {
                            id: s.category_id ?? '',
                            name: s.category?.name ?? null,
                            description: s.category?.description ?? null,
                            icon: s.category?.icon ?? null,
                            items: [],
                          };
                        setEditing({ mode: 'edit', category, item: s });
                      }}
                      disabled={readOnly}
                      className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-panel disabled:cursor-default disabled:hover:bg-white"
                    >
                      <span className="flex items-center gap-2 text-sm text-ink">
                        {s.name || s.category?.name || 'Savings'}
                        <span className="text-[0.66rem] uppercase tracking-wide text-muted">
                          {s.recurring ? intervalLabel(s.recurring_interval) : 'One-time'}
                        </span>
                      </span>
                      <span className="text-sm font-semibold text-ink">
                        {formatCurrency(s.savings)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {editing && !readOnly && (
        <div className="mt-3">
          <SavingsDetailPanel
            category={editing.category}
            item={editing.mode === 'edit' ? editing.item : null}
            formatCurrency={formatCurrency}
            onSaveCreate={onCreate}
            onSaveUpdate={onUpdate}
            onDelete={onDelete}
            onClose={() => setEditing(null)}
          />
        </div>
      )}
    </div>
  );
}
